import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

export interface GroupChat {
  id: string;
  name: string;
  description?: string;
  creatorProfileId?: string;
  creatorId: string;
  isPublic: boolean;
  maxMembers?: number;
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
  members?: GroupChatMember[];
  _count?: {
    members: number;
    messages: number;
  };
}

export interface GroupChatMember {
  id: string;
  chatId: string;
  userId: string;
  role: 'MEMBER' | 'MODERATOR' | 'ADMIN';
  joinedAt: string;
  user?: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
}

export interface GroupChatMessage {
  id: string;
  chatId: string;
  senderId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  sender?: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
}

export interface CreateGroupChatPayload {
  name?: string;
  description?: string;
  creatorProfileId?: string;
  isPublic?: boolean;
  maxMembers?: number;
}

export const groupChatService = {
  async getMyGroupChats() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/group-chats/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { chats: GroupChat[] };
  },

  async getGroupChat(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/group-chats/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { chat: GroupChat };
  },

  async createGroupChat(payload: CreateGroupChatPayload) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/group-chats`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { message: string; chat: GroupChat };
  },

  async updateGroupChat(id: string, payload: Partial<CreateGroupChatPayload>) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/group-chats/${id}`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { message: string; chat: GroupChat };
  },

  async deleteGroupChat(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.delete(`${API_BASE_URL}/api/group-chats/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { message: string };
  },

  async joinGroupChat(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/group-chats/${id}/join`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { message: string; member: GroupChatMember };
  },

  async leaveGroupChat(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/group-chats/${id}/leave`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { message: string };
  },

  async getMessages(id: string, params?: { limit?: number; offset?: number }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/group-chats/${id}/messages`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { limit: 50, offset: 0, ...params },
    });
    return res.data as {
      messages: GroupChatMessage[];
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    };
  },

  async sendMessage(id: string, content: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(
      `${API_BASE_URL}/api/group-chats/${id}/messages`,
      { content },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return res.data as { message: GroupChatMessage };
  },
};


